import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useField } from '@unform/core';
import { FiAlertCircle } from 'react-icons/fi';

import { SelectContainer, Title, Error } from './styles';

interface OptionProps {
  value: string;
  label: string;
}

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  name: string;
  label?: string;
  defaultOption?: string;
  disabled?: boolean;
  options: OptionProps[];
}

const Select: React.FC<InputProps> = ({
  name,
  label,
  defaultOption,
  disabled,
  options,
  style,
  ...rest
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [selected, setSelected] = useState<string>(defaultOption || '');
  const [inputElement, setInputElement] = useState<HTMLElement>();

  const { fieldName, error, registerField } = useField(name);

  const handleDetailsDropDown = useCallback(() => {
    const details = document.getElementById(name.trim()) as HTMLElement;

    if (details.hasAttribute('open')) details.removeAttribute('open');
    else details.setAttribute('open', '');
  }, [name]);

  const handleSelect = useCallback(
    (choice: string) => {
      setSelected(choice);
      handleDetailsDropDown();
    },
    [handleDetailsDropDown],
  );

  useEffect(() => {
    if (defaultOption?.toString()) {
      const inputElement = document.getElementById(
        `${name}${defaultOption}`,
      ) as HTMLElement;

      setInputElement(inputElement);
    }
  }, [defaultOption, name]);

  useEffect(() => {
    if (inputElement && defaultOption?.toString()) {
      inputElement.setAttribute('checked', '');
      setSelected(defaultOption);
    }
  }, [defaultOption, inputElement]);

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,
      path: 'value',
    });
  }, [fieldName, registerField]);

  return (
    <SelectContainer isChecked={!!(defaultOption?.toString() || selected)} style={style}>
      <Title>
        <label className="label" htmlFor={name.trim()}>
          {label}:
        </label>

        {error && (
            <Error title={error}>
              <FiAlertCircle color="#cd351d" size={20} />
            </Error>
          )}
      </Title>
      <details className="custom-select" id={name.trim()}>
        <summary className="radios">
          {!defaultOption?.toString() && !selected
            ? 'Clique para selecionar'
            : ''}

          {options.map(option => (
            <input
              key={option.value}
              type="radio"
              name={name.trim()}
              id={`${name}${option.value.trim()}`}
              title={option.label}
              onClick={() => handleSelect(option.value)}
              value = {option.value.trim()}
              {...rest}
            />
          ))}
        </summary>
        {!disabled && (
          <div className="scroll">
            <ul className="list">
              {options.map(option => (
                <li key={option.value}>
                  <label htmlFor={`${name}${option.value.trim()}`}>{option.label}</label>
                </li>
              ))}
            </ul>
          </div>
        )}
      </details>
      <input ref={inputRef} type="hidden" value={selected} />
    </SelectContainer>
  );
};

export default Select;
